import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import IndexSidebar from 'flarum/forum/components/IndexSidebar';
import LinkButton from 'flarum/common/components/LinkButton';
import { AtriumImage, AtriumAlbum } from './models';
import CommentNotification from './components/CommentNotification';
import LikeNotification from './components/LikeNotification';
import NewPictureNotification from './components/NewPictureNotification';

/*
 * The pages load on demand (see atrium-gallery.js). One chunk for all four, so moving
 * from the gallery to an album to a picture costs a single request.
 */
const page = name => () => import(/* webpackChunkName: 'forum/atrium-gallery', webpackMode: 'lazy-once' */ './atrium-gallery').then(pages => ({
  default: pages[name]
}));
const BrowsePage = page('BrowsePage');
const AlbumPage = page('AlbumPage');
const ImagePage = page('ImagePage');
const QueuePage = page('QueuePage');

/**
 * Atrium, on the forum side.
 *
 * 🚨 Every route here matches one declared in extend.php. A route that exists
 * only in JavaScript works when you click to it and 404s when you paste the
 * link — which is every link anybody shares.
 */
app.initializers.add('ernestdefoe-atrium', () => {
  app.routes['atrium.index'] = {
    path: '/gallery',
    component: BrowsePage
  };
  app.routes['atrium.category'] = {
    path: '/gallery/c/:slug',
    component: BrowsePage
  };
  app.routes['atrium.user'] = {
    path: '/gallery/u/:username',
    component: BrowsePage
  };
  /*
   * 🚨 `:tagSlug`, never `:tag`.
   *
   * Mithril passes route parameters to the component as ATTRS, and `tag` is
   * one of its own reserved attribute names — it throws
   * "You cannot use the \"tag\" attribute name with Mithril 2" and the page
   * renders as nothing at all. Nothing in the error mentions routing.
   */
  app.routes['atrium.tag'] = {
    path: '/gallery/t/:tagSlug',
    component: BrowsePage
  };
  app.routes['atrium.album'] = {
    path: '/gallery/a/:slug',
    component: AlbumPage
  };
  app.routes['atrium.image'] = {
    path: '/gallery/i/:id',
    component: ImagePage
  };
  app.routes['atrium.queue.page'] = {
    path: '/gallery/queue',
    component: QueuePage
  };

  /*
   * 🚨 Both halves of this are load-bearing, and both fail far from home.
   *
   * The store has no fallback model: `/api/notifications` includes each
   * notification's subject, and an unregistered type throws "is not a
   * constructor" while the list is being built — taking down everybody's
   * notifications, not only the gallery's.
   *
   * A missing `notificationComponents` entry is the quieter half: the
   * notification arrives, counts towards the unread badge, and renders as
   * nothing at all.
   */
  app.store.models['atrium-images'] = AtriumImage;
  app.store.models['atrium-albums'] = AtriumAlbum;
  app.notificationComponents.atriumComment = CommentNotification;
  app.notificationComponents.atriumLike = LikeNotification;
  app.notificationComponents.atriumNewPicture = NewPictureNotification;

  /*
   * 🚨 Three rows in the member's own notification settings, because a
   * notification somebody cannot switch off is a notification they switch off
   * by leaving. The row names must match the blueprint types exactly — the
   * grid writes preferences under `notify_<name>_<method>`, and a typo makes a
   * checkbox that saves a preference nothing ever reads.
   */
  extend('flarum/forum/components/NotificationGrid', 'notificationTypes', function (items) {
    items.add('atriumComment', {
      name: 'atriumComment',
      icon: 'fas fa-comment',
      label: app.translator.trans('ernestdefoe-atrium.forum.settings.notify_comment_label')
    });
    items.add('atriumLike', {
      name: 'atriumLike',
      icon: 'fas fa-heart',
      label: app.translator.trans('ernestdefoe-atrium.forum.settings.notify_like_label')
    });
    items.add('atriumNewPicture', {
      name: 'atriumNewPicture',
      icon: 'fas fa-images',
      label: app.translator.trans('ernestdefoe-atrium.forum.settings.notify_new_picture_label')
    });
  });

  /*
   * 🚨 `IndexSidebar`, not `IndexPage`.
   *
   * Flarum 2 moved the sidebar out of IndexPage, and IndexPage still HAS a
   * `navItems` — so extending the wrong one is not an error anywhere: the
   * callback simply never runs, the link never appears, and the gallery looks
   * like it failed to install.
   *
   * 🚨 ONE link, here, with the rest of the forum's navigation. A header item
   * as well was briefly in — added while this one was silently doing nothing —
   * and two ways into the same page a few centimetres apart reads as an
   * extension that was never looked at.
   */
  extend(IndexSidebar.prototype, 'navItems', function (items) {
    if (!app.forum.attribute('canViewAtrium')) return;
    items.add('atrium', LinkButton.component({
      href: app.route('atrium.index'),
      icon: 'fas fa-images'
    }, app.translator.trans('ernestdefoe-atrium.forum.nav')), 5);
  });
});flarum.reg.addChunkModule('ernestdefoe-atrium:forum/atrium-gallery', '116', 'ernestdefoe-atrium', 'forum/atrium-gallery');
flarum.reg.addChunkModule('ernestdefoe-atrium:forum/atrium-gallery', '116', 'ernestdefoe-atrium', 'forum/components/BrowsePage');
flarum.reg.addChunkModule('ernestdefoe-atrium:forum/atrium-gallery', '116', 'ernestdefoe-atrium', 'forum/components/AlbumPage');
flarum.reg.addChunkModule('ernestdefoe-atrium:forum/atrium-gallery', '116', 'ernestdefoe-atrium', 'forum/components/ImagePage');
flarum.reg.addChunkModule('ernestdefoe-atrium:forum/atrium-gallery', '116', 'ernestdefoe-atrium', 'forum/components/QueuePage');
flarum.reg.addChunkModule('ernestdefoe-atrium:forum/atrium-gallery', '116', 'ernestdefoe-atrium', 'forum/components/Grid');
flarum.reg.addChunkModule('ernestdefoe-atrium:forum/atrium-gallery', '116', 'ernestdefoe-atrium', 'forum/components/AlbumCard');
flarum.reg.addChunkModule('ernestdefoe-atrium:forum/atrium-gallery', '116', 'ernestdefoe-atrium', 'forum/components/AlbumModal');
flarum.reg.addChunkModule('ernestdefoe-atrium:forum/atrium-gallery', '116', 'ernestdefoe-atrium', 'forum/components/Skeleton');
flarum.reg.addChunkModule('ernestdefoe-atrium:forum/atrium-gallery', '116', 'ernestdefoe-atrium', 'forum/components/Aperture');
flarum.reg.addChunkModule('ernestdefoe-atrium:forum/atrium-gallery', '116', 'ernestdefoe-atrium', 'forum/components/ArrangeModal');
flarum.reg.addChunkModule('ernestdefoe-atrium:forum/atrium-gallery', '116', 'ernestdefoe-atrium', 'forum/components/UploadModal');
flarum.reg.addChunkModule('ernestdefoe-atrium:forum/atrium-gallery', '116', 'ernestdefoe-atrium', 'forum/components/Stage');
flarum.reg.addChunkModule('ernestdefoe-atrium:forum/atrium-gallery', '116', 'ernestdefoe-atrium', 'forum/components/Filmstrip');
flarum.reg.addChunkModule('ernestdefoe-atrium:forum/atrium-gallery', '116', 'ernestdefoe-atrium', 'forum/components/Photo');